import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, isBefore, startOfToday } from 'date-fns'
import { ChevronLeft, ChevronRight, X, PlusCircle, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TimePickerCombobox } from '@/components/ui/TimePickerCombobox'
import { cn } from '@/lib/utils'
import type { TimeSlot, ArtistAvailability } from '@/types/booking'

interface MonthlyAvailabilityProps {
  availability?: ArtistAvailability | null
  onSave: (dates: Date[], timeSlots: TimeSlot[], isUnavailable: boolean) => void
  useMonthSchedule?: boolean
}

export function MonthlyAvailability({
  availability,
  onSave,
  useMonthSchedule = false
}: MonthlyAvailabilityProps) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDates, setSelectedDates] = useState<Date[]>([])
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([{ start: 540, end: 1020 }])
  const [isSuccess, setIsSuccess] = useState(false)
  const [mode, setMode] = useState<'new' | 'edit' | null>(null)
  const [isUnavailable, setIsUnavailable] = useState(false)

  const handlePrevMonth = () => setCurrentDate(prev => subMonths(prev, 1))
  const handleNextMonth = () => setCurrentDate(prev => addMonths(prev, 1))

  const getDateOverride = (date: Date) => {
    const dateString = format(date, 'yyyy-MM-dd')
    if (useMonthSchedule) {
      return availability?.monthSchedule?.[dateString] || null
    }
    return availability?.dateOverrides?.[dateString] || null
  }

  const handleDayClick = (date: Date) => {
    const override = getDateOverride(date)
    const isDateOverridden = !!override
    
    setSelectedDates(prev => {
      const isSelected = prev.some(d => isSameDay(d, date))
      
      if (isSelected) {
        const newSelectedDates = prev.filter(d => !isSameDay(d, date))
        if (newSelectedDates.length === 0) {
          setMode(null)
          setIsUnavailable(false)
        }
        return newSelectedDates
      } else {
        if (prev.length === 0) {
          if (isDateOverridden) {
            setMode('edit')
            setTimeSlots(override.timeSlots)
          } else {
            setMode('new')
          }
        } else {
          const currentMode = mode
          if (currentMode === 'edit' && !isDateOverridden) return prev
          if (currentMode === 'new' && isDateOverridden) return prev
          if (currentMode === 'edit' && isDateOverridden) {
            setTimeSlots(override.timeSlots)
          }
        }
        return [...prev, date]
      }
    })
  }

  const handleAddTimeSlot = () => {
    setTimeSlots([...timeSlots, { start: 540, end: 1020 }])
  }

  const handleUpdateTimeSlot = (index: number, timeSlot: TimeSlot) => {
    setTimeSlots(timeSlots.map((slot, i) => i === index ? timeSlot : slot))
  }

  const handleRemoveTimeSlot = (index: number) => {
    setTimeSlots(timeSlots.filter((_, i) => i !== index))
  }

  const handleSave = () => {
    const slotsToSave = isUnavailable ? [] : timeSlots
    onSave(selectedDates, slotsToSave, isUnavailable)
    setIsSuccess(true)
    setTimeout(() => {
      setSelectedDates([])
      setTimeSlots([{ start: 540, end: 1020 }])
      setIsUnavailable(false)
      setIsSuccess(false)
      setMode(null)
    }, 1500)
  }

  const handleClear = () => {
    setSelectedDates([])
    setTimeSlots([{ start: 540, end: 1020 }])
    setIsUnavailable(false)
    setMode(null)
  }

  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd })

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const startPadding = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1
  const paddingDays = Array.from({ length: startPadding }, () => null)

  return (
    <div className="flex flex-col gap-2">
      {/* Calendar Section */}
      <div className="bg-card rounded-lg p-2">
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handlePrevMonth}
            className="p-2 rounded-full hover:bg-muted"
          >
            <ChevronLeft className="w-5 h-5" />
          </motion.button>
          
          <h2 className="text-lg font-semibold">
            {format(currentDate, 'MMMM yyyy')}
          </h2>
          
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleNextMonth}
            className="p-2 rounded-full hover:bg-muted"
          >
            <ChevronRight className="w-5 h-5" />
          </motion.button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Day Headers */}
          {dayNames.map(day => (
            <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
              {day}
            </div>
          ))}
          
          {/* Padding Days */}
          {paddingDays.map((_, i) => (
            <div key={`pad-${i}`} className="aspect-square" />
          ))}
          
          {/* Calendar Days */}
          {daysInMonth.map(date => {
            const isSelected = selectedDates.some(d => isSameDay(d, date))
            const isPast = isBefore(date, startOfToday())
            const override = getDateOverride(date)
            const isOverridden = !!override
            
            return (
              <button
                key={date.toISOString()}
                onClick={() => !isPast && handleDayClick(date)}
                disabled={isPast}
                className={cn(
                  "aspect-square rounded-lg flex items-center justify-center text-sm font-medium transition-all",
                  isSelected && "bg-primary text-primary-foreground",
                  !isSelected && !isPast && "hover:bg-muted",
                  isToday(date) && !isSelected && "ring-2 ring-primary ring-inset",
                  !isSameMonth(date, currentDate) && "text-muted-foreground opacity-50",
                  isPast && "opacity-40 cursor-not-allowed",
                  isOverridden && !isSelected && "bg-muted"
                )}
              >
                {format(date, 'd')}
              </button>
            )
          })}
        </div>
      </div>

      {/* Time Slots Section */}
      <div className={cn(
        "bg-card rounded-lg p-2 transition-opacity",
        selectedDates.length === 0 && "opacity-50 pointer-events-none"
      )}>
        <h3 className="text-sm font-semibold mb-3">
          {selectedDates.length === 0 
            ? 'Select dates to set availability'
            : mode === 'edit'
              ? `Edit ${selectedDates.length} ${selectedDates.length === 1 ? 'date' : 'dates'}`
              : `Set times for ${selectedDates.length} ${selectedDates.length === 1 ? 'date' : 'dates'}`
          }
        </h3>

        {isUnavailable ? (
          <div className="flex items-center justify-between p-2 bg-muted rounded-lg mb-3">
            <span className="text-xs text-muted-foreground">
              Marked as unavailable
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setIsUnavailable(false)
                setTimeSlots([{ start: 540, end: 1020 }])
              }}
            >
              <PlusCircle className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="space-y-2 mb-3">
            {timeSlots.map((slot, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex-1 flex items-center gap-2">
                  <TimePickerCombobox
                    value={slot.start}
                    onChange={(value) => handleUpdateTimeSlot(index, { ...slot, start: value })}
                    otherTimeSlots={timeSlots.filter((_, i) => i !== index)}
                    isStartTime={true}
                  />
                  <span className="text-muted-foreground">-</span>
                  <TimePickerCombobox
                    value={slot.end}
                    onChange={(value) => handleUpdateTimeSlot(index, { ...slot, end: value })}
                    otherTimeSlots={timeSlots.filter((_, i) => i !== index)}
                    isStartTime={false}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (timeSlots.length === 1) {
                        handleRemoveTimeSlot(index)
                        setIsUnavailable(true)
                      } else {
                        handleRemoveTimeSlot(index)
                      }
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  {index === timeSlots.length - 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleAddTimeSlot}
                    >
                      <PlusCircle className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleClear}
            className="flex-1"
            disabled={selectedDates.length === 0}
          >
            Clear
          </Button>
          <Button 
            onClick={handleSave}
            disabled={isSuccess || selectedDates.length === 0}
            className="flex-1"
          >
            <AnimatePresence mode="wait">
              {isSuccess ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  className="flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Saved!
                </motion.div>
              ) : (
                <motion.span
                  key="save"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  Save
                </motion.span>
              )}
            </AnimatePresence>
          </Button>
        </div>
      </div>
    </div>
  )
}
